import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Star, MapPin, Heart, Check, Calendar, Clock, Users,
  Minus, Plus, Ticket, Info, Sparkles, CheckCircle2, PartyPopper, Navigation,
} from "lucide-react";
import { getDestination, COORDS } from "../data/destinations";
import { useApp } from "../context/AppState";
import { rupiah } from "../lib/format";

const TIMES = ["10:00", "12:00", "14:00", "16:00", "18:00", "20:00"];

export default function DestinationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dest = getDestination(id);
  const { state, toggleSaved, createBooking, completeBooking } = useApp();

  const [activeImg, setActiveImg] = useState(0);
  const [date, setDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [time, setTime] = useState("18:00");
  const [guests, setGuests] = useState(2);
  const [voucherId, setVoucherId] = useState("");
  const [done, setDone] = useState(null); // booking id

  const usableVouchers = useMemo(
    () => (state?.vouchers || []).filter((v) => !v.used),
    [state]
  );

  if (!dest) return <div className="p-10 text-center">Destinasi tidak ditemukan.</div>;

  const saved = state?.saved?.includes(dest.id);
  const base = dest.price * guests;
  const selectedVoucher = usableVouchers.find((v) => v.id === voucherId);
  const eligible = selectedVoucher && base >= selectedVoucher.minSpend;
  const discount = eligible ? selectedVoucher.value : 0;
  const total = Math.max(0, base - discount);
  const pointsPreview = Math.floor(total / 1000);

  const confirm = () => {
    if (voucherId && selectedVoucher && !eligible) {
      toast.error(`Voucher butuh min. transaksi ${rupiah(selectedVoucher.minSpend)}`);
      return;
    }
    const bid = createBooking({ destId: dest.id, date, time, guests, voucherId: eligible ? voucherId : null });
    setDone(bid);
    toast.success("Reservasi dikonfirmasi! 🎉");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const simulate = () => {
    completeBooking(done);
    toast.success(`Kunjungan selesai! +${pointsPreview} poin ✨`);
    navigate("/cerita");
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg py-8 text-center kk-reveal">
        <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-[#e6f0e8] text-[#4e8a5f]">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-extrabold">Reservasi terkonfirmasi</h1>
        <p className="mx-auto mt-2 max-w-sm text-[14px] text-[#8a8278]">
          Booking <b>{done}</b> tersimpan di Cerita kita. Ini demo — tidak ada pembayaran nyata.
        </p>

        <div className="mt-6 rounded-2xl border border-[#efeae4] bg-white p-5 text-left">
          <div className="flex items-center gap-3">
            <img src={dest.img} alt="" className="h-16 w-20 rounded-xl object-cover" />
            <div>
              <div className="text-[15px] font-bold">{dest.name}</div>
              <div className="text-[13px] text-[#8a8278]">{date} · {time} · {guests} orang</div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-[#f2ede8] pt-3 text-[14px]">
            <span className="text-[#8a8278]">Total dibayar</span>
            <b>{rupiah(total)}</b>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-[#fff7ef] p-4 text-left">
          <div className="flex items-center gap-2 text-[13px] font-bold text-[#a5762e]">
            <Sparkles className="h-4 w-4" /> Untuk demo kompetisi
          </div>
          <p className="mt-1 text-[12px] text-[#9a7f5c]">
            Simulasikan kunjungan selesai untuk langsung mendapat <b>{pointsPreview} poin</b> & XP untuk Mochi.
          </p>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            onClick={simulate}
            data-testid="simulate-visit"
            className="flex items-center justify-center gap-2 rounded-full kk-coral-gradient py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.98]"
          >
            <PartyPopper className="h-4 w-4" /> Simulasikan kunjungan selesai
          </button>
          <button
            onClick={() => navigate("/cerita")}
            data-testid="go-cerita"
            className="rounded-full border border-[#efeae4] bg-white py-3.5 text-[15px] font-bold text-[#7c7277]"
          >
            Lihat di Cerita kita
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kk-reveal">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="mb-4 flex items-center gap-1 text-[14px] font-semibold text-[#7c7277] hover:text-[#1e1b18]"
      >
        <ChevronLeft className="h-4 w-4" /> Kembali
      </button>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* left */}
        <div>
          <div className="overflow-hidden rounded-3xl border border-[#efeae4] bg-white">
            <img src={dest.gallery[activeImg]} alt={dest.name} className="aspect-[16/10] w-full object-cover" />
          </div>
          <div className="mt-3 flex gap-2">
            {dest.gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`h-16 w-24 overflow-hidden rounded-xl border-2 ${activeImg === i ? "border-[#ff5a5f]" : "border-transparent"}`}
              >
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>

          <div className="mt-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-[13px] text-[#8a8278]">
                  <span className="rounded-full bg-[#f5eee8] px-2.5 py-0.5 font-semibold text-[#998379]">{dest.category}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#c9878f]" />{dest.area}, {dest.city}</span>
                </div>
                <h1 className="mt-2 text-3xl font-extrabold tracking-tight">{dest.name}</h1>
              </div>
              <button
                onClick={() => { toggleSaved(dest.id); toast(saved ? "Dihapus dari simpanan" : "Disimpan 💕"); }}
                data-testid="detail-save"
                className={`grid h-11 w-11 flex-none place-items-center rounded-full border border-[#efeae4] bg-white ${saved ? "text-[#ff5a5f]" : "text-[#9a8b8f]"}`}
              >
                <Heart className={`h-5 w-5 ${saved ? "fill-[#ff5a5f]" : ""}`} />
              </button>
            </div>

            <div className="mt-3 flex items-center gap-4 text-[14px]">
              <span className="flex items-center gap-1 font-bold"><Star className="h-4 w-4 fill-[#f59e0b] text-[#f59e0b]" />{dest.rating}</span>
              <span className="font-extrabold">{rupiah(dest.price)} <span className="text-[12px] font-medium text-[#a89f98]">/ berdua</span></span>
            </div>

            <a
              href={COORDS[dest.id]
                ? `https://www.google.com/maps/dir/?api=1&destination=${COORDS[dest.id][0]},${COORDS[dest.id][1]}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${dest.name} ${dest.area} ${dest.city}`)}`}
              target="_blank" rel="noreferrer" data-testid="open-maps"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#dbe6ff] bg-[#eff4ff] px-4 py-2.5 text-[13px] font-bold text-[#4f74c8] transition-colors hover:bg-[#e2ecff]"
            >
              <Navigation className="h-4 w-4" /> Arahkan ke sini
            </a>

            <p className="mt-4 text-[15px] leading-relaxed text-[#575048]">{dest.desc}</p>

            <h3 className="mt-6 text-[15px] font-bold">Yang kalian dapat</h3>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {dest.amenities.map((a) => (
                <div key={a} className="flex items-center gap-2 text-[14px] text-[#575048]">
                  <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-[#e6f0e8] text-[#4e8a5f]"><Check className="h-3.5 w-3.5" /></span>
                  {a}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right — booking */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-3xl border border-[#eae0d7] bg-[#f9f5f0] p-5">
            <h3 className="text-[17px] font-extrabold">Atur reservasi</h3>
            <p className="mb-4 flex items-center gap-1.5 text-[12px] text-[#a08d80]">
              <Info className="h-3.5 w-3.5" /> Booking simulasi — tanpa pembayaran nyata.
            </p>

            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Tanggal</label>
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#e8dfd7] bg-white px-3">
              <Calendar className="h-4 w-4 text-[#c9878f]" />
              <input type="date" value={date} min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)} data-testid="booking-date"
                className="w-full bg-transparent py-3 text-[14px] outline-none" />
            </div>

            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Jam</label>
            <div className="mb-3 grid grid-cols-3 gap-2">
              {TIMES.map((t) => (
                <button key={t} onClick={() => setTime(t)} data-testid={`time-${t}`}
                  className={`flex items-center justify-center gap-1 rounded-xl border py-2.5 text-[13px] font-semibold ${
                    time === t ? "border-[#ff5a5f] bg-[#ffebeb] text-[#ff5a5f]" : "border-[#e8dfd7] bg-white text-[#7c7277]"
                  }`}>
                  <Clock className="h-3 w-3" />{t}
                </button>
              ))}
            </div>

            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Jumlah orang</label>
            <div className="mb-3 flex items-center justify-between rounded-xl border border-[#e8dfd7] bg-white px-3 py-2">
              <span className="flex items-center gap-2 text-[14px]"><Users className="h-4 w-4 text-[#c9878f]" />{guests} orang</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setGuests((g) => Math.max(2, g - 1))} data-testid="guests-minus"
                  className="grid h-8 w-8 place-items-center rounded-lg bg-[#f7f0ea]"><Minus className="h-3.5 w-3.5" /></button>
                <button onClick={() => setGuests((g) => Math.min(10, g + 1))} data-testid="guests-plus"
                  className="grid h-8 w-8 place-items-center rounded-lg bg-[#f7f0ea]"><Plus className="h-3.5 w-3.5" /></button>
              </div>
            </div>

            <label className="mb-1.5 block text-[12px] font-semibold text-[#8f7c7c]">Voucher</label>
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#e8dfd7] bg-white px-3">
              <Ticket className="h-4 w-4 text-[#c9878f]" />
              <select value={voucherId} onChange={(e) => setVoucherId(e.target.value)} data-testid="voucher-select"
                className="w-full bg-transparent py-3 text-[14px] outline-none">
                <option value="">Tanpa voucher</option>
                {usableVouchers.map((v) => (
                  <option key={v.id} value={v.id}>{v.label} (min {rupiah(v.minSpend)})</option>
                ))}
              </select>
            </div>

            <div className="border-t border-dashed border-[#ddcec4] pt-3">
              <div className="flex justify-between text-[13px] text-[#8a7f7c]"><span>Subtotal ({guests} orang)</span><span>{rupiah(base)}</span></div>
              {discount > 0 && (
                <div className="mt-1 flex justify-between text-[13px] font-semibold text-[#ff5a5f]"><span>Diskon voucher</span><span>−{rupiah(discount)}</span></div>
              )}
              {selectedVoucher && !eligible && (
                <div className="mt-1 text-[12px] text-[#c9363b]">Min. transaksi {rupiah(selectedVoucher.minSpend)} belum terpenuhi.</div>
              )}
              <div className="mt-2 flex items-center justify-between border-t border-[#eee4db] pt-2 text-[18px] font-extrabold">
                <span className="text-[13px] font-semibold text-[#575048]">Total</span>
                <span>{rupiah(total)}</span>
              </div>
              <p className="mt-1 flex items-center justify-end gap-1 text-[12px] font-semibold text-[#9a986b]">
                <Sparkles className="h-3.5 w-3.5" /> +{pointsPreview} poin saat kunjungan selesai
              </p>
            </div>

            <button
              onClick={confirm}
              data-testid="confirm-booking"
              className="mt-4 w-full rounded-full kk-coral-gradient py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.98]"
            >
              Konfirmasi reservasi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
